import React, { useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { MessageSquareIcon, EyeIcon, CodeIcon, SettingsIcon, BarChart2Icon, PlayIcon, AlertCircleIcon } from 'lucide-react';
import { ChatbotSettingsModal } from './ChatbotSettingsModal';
import { ChatbotPreviewModal } from './ChatbotPreviewModal';
import { ChatbotEmbedModal } from './ChatbotEmbedModal';
import { toast } from 'sonner';
import { ERROR_MESSAGES } from '../../../config/error-messages';
import { useNavigate, useParams } from 'react-router-dom';
interface Chatbot {
  id: string;
  name: string;
  personality: string;
  createdAt: string;
  status: 'active' | 'inactive';
}
interface Props {
  chatbot: Chatbot;
  onError: (error: Error) => void;
}
export const ChatbotCard = ({
  chatbot,
  onError
}: Props) => {
  const navigate = useNavigate();
  const {
    id
  } = useParams(); // Get the space ID from URL
  // Update navigation to stay within space context
  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState<'settings' | 'analytics' | 'test'>('settings');
  const [isLoading, setIsLoading] = useState(false);
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const handleUpdateChatbot = async (data: any) => {
    try {
      setIsLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Update successful
      setShowSettings(false);
      toast.success('Chatbot updated successfully');
    } catch (error) {
      onError(error as Error);
      toast.error(ERROR_MESSAGES.api.serverError);
    } finally {
      setIsLoading(false);
    }
  };
  const handleOpenModal = async (tab: 'settings' | 'analytics' | 'test') => {
    try {
      navigate(`/space/${id}/chatbot/${chatbot.id}`);
    } catch (error) {
      onError(error as Error);
      toast.error(ERROR_MESSAGES.api.serverError);
    }
  };
  if (isLoading) {
    return <div className="bg-surface-light border border-surface-border p-6 rounded-none" data-id="element-704">
        <div className="flex items-center justify-center" data-id="element-705">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" data-id="element-706" />
        </div>
      </div>;
  }
  return <>
      <div className="bg-surface-light border border-surface-border p-4 sm:p-6 rounded-none group hover:bg-surface-mid transition-colors duration-200" data-id="element-707">
        <div className="flex items-start justify-between" data-id="element-708">
          <div className="space-y-3 flex-1" data-id="element-709">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-710">
              <h3 className="text-lg font-medium text-white font-mono" data-id="element-711">
                {chatbot.name}
              </h3>
              <StatusBadge status={chatbot.status} data-id="element-712" />
            </div>
            <p className="text-sm text-gray-400" data-id="element-713">{chatbot.personality}</p>
            <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap" data-id="element-714">
              <span className="font-mono" data-id="element-715">
                Created {formatDate(chatbot.createdAt)}
              </span>
              <div className="flex items-center gap-1.5" data-id="element-716">
                <MessageSquareIcon size={14} className="text-gray-500" data-id="element-717" />
                <span className="font-mono text-xs uppercase tracking-wide" data-id="element-718">
                  Uses all Knowledge Space documents
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Actions */}
        <div className="mt-4 pt-4 border-t border-surface-border block sm:hidden" data-id="element-719">
          <div className="grid grid-cols-2 gap-2" data-id="element-720">
            <button onClick={() => handleOpenModal('settings')} className="flex items-center justify-center gap-2 p-2 text-sm text-gray-400 hover:text-accent-light transition-colors border border-surface-border rounded-none" data-id="element-721">
              <SettingsIcon size={16} data-id="element-722" />
              <span className="font-mono uppercase tracking-wide" data-id="element-723">
                Configure
              </span>
            </button>
            <button onClick={() => handleOpenModal('test')} className="flex items-center justify-center gap-2 p-2 text-sm text-gray-400 hover:text-accent-light transition-colors border border-surface-border rounded-none" data-id="element-724">
              <PlayIcon size={16} data-id="element-725" />
              <span className="font-mono uppercase tracking-wide" data-id="element-726">Test</span>
            </button>
          </div>
        </div>
        {/* Desktop Actions */}
        <div className="mt-6 pt-4 border-t border-surface-border hidden sm:block" data-id="element-727">
          <div className="flex justify-between items-center" data-id="element-728">
            <div className="flex items-center gap-6" data-id="element-729">
              <button onClick={() => handleOpenModal('settings')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-accent-light transition-colors group/button" data-id="element-730">
                <SettingsIcon size={16} className="group-hover/button:rotate-90 transition-transform duration-200" data-id="element-731" />
                <span className="font-mono uppercase tracking-wide" data-id="element-732">
                  Configure
                </span>
              </button>
              <button onClick={() => handleOpenModal('analytics')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-accent-light transition-colors group/button" data-id="element-733">
                <BarChart2Icon size={16} className="group-hover/button:translate-y-[-2px] transition-transform duration-200" data-id="element-734" />
                <span className="font-mono uppercase tracking-wide" data-id="element-735">
                  Analytics
                </span>
              </button>
              <button onClick={() => handleOpenModal('test')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-accent-light transition-colors group/button" data-id="element-736">
                <PlayIcon size={16} className="group-hover/button:translate-x-1 transition-transform duration-200" data-id="element-737" />
                <span className="font-mono uppercase tracking-wide" data-id="element-738">
                  Test Chat
                </span>
              </button>
            </div>
            <div className="flex items-center gap-3 border-l border-surface-border pl-6" data-id="element-739">
              <button onClick={() => setShowPreview(true)} className="p-2 text-gray-400 hover:text-accent-light hover:bg-surface transition-colors rounded-none" title="Preview embedded chatbot" data-id="element-740">
                <EyeIcon size={18} data-id="element-741" />
              </button>
              <button onClick={() => setShowEmbed(true)} className="p-2 text-gray-400 hover:text-accent-light hover:bg-surface transition-colors rounded-none" title="Get embed code" data-id="element-742">
                <CodeIcon size={18} data-id="element-743" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <ChatbotSettingsModal isOpen={showSettings} onClose={() => {
      setShowSettings(false);
      // Reset to default tab after closing
      setTimeout(() => {
        setActiveModalTab('settings');
      }, 200); // After close animation
    }} chatbot={chatbot} onUpdate={handleUpdateChatbot} initialTab={activeModalTab} data-id="element-744" />
      <ChatbotPreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} chatbot={chatbot} data-id="element-745" />
      <ChatbotEmbedModal isOpen={showEmbed} onClose={() => setShowEmbed(false)} chatbot={chatbot} data-id="element-746" />
    </>;
};